import React, { useRef, useState } from 'react';
import { PersonFill, PencilFill, ChevronDown } from 'react-bootstrap-icons';

// --- IMPORTS ---
import { useUser } from '../../contextManager/user.context';
import useAuth, { ProfilePatch } from '../../hooks/auth.service';
import { ActionButton, PhotoWrapper, StyledImage } from '../../styles/profile/profile.style';
import { PhotoStep, PhotoStepHandle } from '../../pages/steps/PhotoStep';

// Import the Layout Wrappers
import { 
  AccordionItemWrapper, 
  CardHeader, 
  CardContent, 
  CardFooter, // <--- Correct import from layout.style
  HeaderIconButton, 
  ChevronToggle 
} from '../../styles/profile/layout.style';

interface ProfilePhotoProps {
  isOpen: boolean;
  onToggle: () => void;
  isMobile?: boolean; 
}

export const ProfilePhoto: React.FC<ProfilePhotoProps> = ({ isOpen, onToggle, isMobile = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingPatch, setPendingPatch] = useState<ProfilePatch | null>(null);
    
    const { userDetails } = useUser();
    const { patchProfile } = useAuth();
    const photoStepRef = useRef<PhotoStepHandle>(null);
  
    const handleSave = async () => {
        setIsSaving(true);
        try {
          const file = await photoStepRef.current?.saveIfNeeded();
          
          if (file) {
            await patchProfile(
              { profilePhoto: file },
              { loading: 'Saving photo...', success: 'Photo saved.' },
            );
          }
        } catch (error) {
          console.error(error);
        } finally {
          setPendingPatch(null);
          setIsSaving(false);
          setIsEditing(false);
        }
      };
      
    return (
      <AccordionItemWrapper 
        isOpen={isOpen}
        autoHeight={isOpen && !isEditing}
      >
        
        {/* 1. HEADER */}
        <CardHeader 
            onClick={onToggle} 
            collapsed={!isOpen}
            css={{ cursor: 'pointer' }} 
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {!isMobile && 
                <ChevronToggle collapsed={!isOpen}>
                    <ChevronDown />
                </ChevronToggle>
                }
                <span>{isEditing ? 'Edit Photo' : 'Profile Photo'}</span>
            </div>

            <div onClick={(e) => e.stopPropagation()}>
                 {isOpen && !isEditing && (
                    <HeaderIconButton onClick={() => setIsEditing(true)}>
                        <PencilFill size={18} />
                    </HeaderIconButton>
                 )}
            </div>
        </CardHeader>

        {/* 2. BODY */}
        {isOpen && (
          isEditing ? (
            <>
              <CardContent>
                <PhotoStep
                  ref={photoStepRef}
                  isStandalone={true}
                  data={userDetails || {}}
                  onChange={(patch: ProfilePatch) => setPendingPatch(patch)}
                />
              </CardContent>
              <CardFooter>
                <ActionButton intent="secondary" onClick={() => { setIsEditing(false); setPendingPatch(null); }} disabled={isSaving}>
                  Cancel
                </ActionButton>
                <ActionButton intent="primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </ActionButton>
              </CardFooter>
            </>
            ) : (
                // --- DISPLAY MODE ---
                <CardContent>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem 0' }}>
                        {userDetails?.profilePhotoUrl ? (
                            <PhotoWrapper hasPhoto={true}>
                                <StyledImage src={userDetails.profilePhotoUrl} alt="Profile photo" />
                            </PhotoWrapper>
                        ) : (
                            <PhotoWrapper hasPhoto={false}>
                                <PersonFill size={180} color="#e0e0e0" />
                            </PhotoWrapper>
                        )}
                    </div>
                </CardContent>
            )
        )}
      </AccordionItemWrapper>
    );
};